import { useEffect, useState } from 'react';
import { sharedGet } from './sharedGet';
import { pictureSrc } from './pictureRef';

// The Parts-DB photos this manual produced, for one submodel — what the 3D phase
// reconstructs FROM.
//
// The catalog rather than the raw generation history, deliberately. Every version ever
// generated is kept now, good and bad; the Parts DB is the subset somebody looked at and
// switched on. A reconstruction costs $0.375 and believes whatever it is shown, so the
// curated set is the right input and the toggle in Phase 2 is how you curate it.
export type PartPhoto = {
  id: number;
  itemId: number;
  // Which side of the part it shows. Empty for a photo generated without an angle — every
  // LLM-pipeline one, and anything from before the angle was recorded. Empty is not
  // 'front': it means nobody said, and the slot it would fill is not knowable.
  view: string;
  model: string;
  variant: string;
};

export async function fetchPartPhotos(docId: string, variant: string): Promise<PartPhoto[]> {
  const { data } = await sharedGet<{ photos: PartPhoto[] }>(
    `/api/notice/doc/${encodeURIComponent(docId)}/part-photos`,
    { params: { variant: variant || undefined } },
  );
  return data.photos || [];
}

// The URL that serves one of them. Same route the catalog uses — and the same builder,
// so all four names for a stored picture resolve through pictureRef and pick up the
// build's base path there rather than in four places that could drift.
export const partPhotoSrc = (id: number) => pictureSrc({ source: 'photo', imageId: id });

// Fetch on open, and again whenever `epoch` moves — Phase 2's database switch bumps the
// images epoch, and this phase's inputs are exactly what that switch changes.
export function usePartPhotos(docId: string, variant: string, epoch: number) {
  const [photos, setPhotos] = useState<PartPhoto[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    void fetchPartPhotos(docId, variant)
      .then((p) => {
        if (cancelled) return;
        setPhotos(p);
        setLoaded(true);
      })
      .catch(() => {
        // An unreachable server leaves the phase empty rather than broken; `loaded`
        // stays false, which is what tells the panel to say nothing rather than "no
        // photos yet".
        if (!cancelled) setPhotos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [docId, variant, epoch]);
  return { photos, loaded };
}
